import React, { useEffect, useRef, useState } from "react";
import { Monogram } from "@/components/MemberAvatar";
import { fontOf, textLines, fitFontPx } from "@/components/fliers/flierConfig";

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const LABEL = { photo: "Photo", name: "Name", date: "Date" };

// Median colour of the template pixels framing a zone — mirrors the server's "clear under text" fill
function sampleFrame(img, z) {
  if (!img?.naturalWidth) return null;
  const c = document.createElement("canvas"); c.width = img.naturalWidth; c.height = img.naturalHeight;
  const ctx = c.getContext("2d"); ctx.drawImage(img, 0, 0);
  const x0 = Math.round(z.x * c.width), y0 = Math.round(z.y * c.height), w = Math.round(z.w * c.width), h = Math.round(z.h * c.height);
  const px = [];
  const take = (x, y) => { const d = ctx.getImageData(clamp(x, 0, c.width - 1), clamp(y, 0, c.height - 1), 1, 1).data; px.push([d[0], d[1], d[2]]); };
  for (let x = x0; x < x0 + w; x += 6) { take(x, y0); take(x, y0 + h); }
  for (let y = y0; y < y0 + h; y += 6) { take(x0, y); take(x0 + w, y); }
  if (!px.length) return null;
  const med = (i) => px.map((p) => p[i]).sort((a, b) => a - b)[Math.floor(px.length / 2)];
  return `rgb(${med(0)},${med(1)},${med(2)})`;
}

function TextLayer({ z, text, scale, boxW, boxH }) {
  const f = fontOf(z.font);
  const t = textLines(z, text);
  const [px, setPx] = useState(0);
  useEffect(() => {
    let live = true;
    const measure = () => live && setPx(fitFontPx(t, z.font, boxW, boxH, (z.max_size || 80) * scale));
    measure();
    if (document.fonts?.load) document.fonts.load(`${f.weight} 20px ${f.css}`).then(measure).catch(() => {});
    return () => { live = false; };
  }, [t, z.font, z.max_size, boxW, boxH, scale, f.weight, f.css]);
  return (
    <div className="flex h-full w-full items-center justify-center whitespace-pre text-center leading-[1.08]" style={{ fontFamily: f.css, fontWeight: f.weight, fontSize: px, color: z.color, textShadow: z.shadow ? `${Math.max(1, px / 28)}px ${Math.max(1, px / 28)}px 2px rgba(0,0,0,.6)` : "none" }}>{t}</div>
  );
}

// Live flier canvas: template image + draggable/resizable photo, name and date layers
export default function StudioCanvas({ templateUrl, zones, setZones, photoUrl, name, date, active, setActive }) {
  const box = useRef();
  const imgRef = useRef();
  const drag = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0, nat: 1 });
  const [fills, setFills] = useState({});
  const clearKey = ["name", "date"].map((k) => zones[k] ? `${k}:${zones[k].clear ? 1 : 0}:${zones[k].x.toFixed(3)}:${zones[k].y.toFixed(3)}:${zones[k].w.toFixed(3)}:${zones[k].h.toFixed(3)}` : "").join("|");
  useEffect(() => {
    if (!imgRef.current?.complete || !size.nat) return;
    const f = {};
    ["name", "date"].forEach((k) => { if (zones[k]?.clear) f[k] = sampleFrame(imgRef.current, zones[k]); });
    setFills(f);
  }, [clearKey, size.nat]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!box.current) return;
    const ro = new ResizeObserver(() => { const r = box.current?.getBoundingClientRect(); if (r) setSize((s) => ({ ...s, w: r.width, h: r.height })); });
    ro.observe(box.current);
    return () => ro.disconnect();
  }, [templateUrl]);

  const rel = (e) => { const r = box.current.getBoundingClientRect(); return { x: clamp((e.clientX - r.left) / r.width, 0, 1), y: clamp((e.clientY - r.top) / r.height, 0, 1) }; };
  const down = (e, mode, key) => {
    e.preventDefault(); e.stopPropagation();
    setActive(key);
    drag.current = { mode, key, start: rel(e), orig: { ...zones[key] } };
    box.current.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    const d = drag.current; if (!d) return;
    const p = rel(e), dx = p.x - d.start.x, dy = p.y - d.start.y, o = d.orig;
    let n;
    if (d.mode === "move") n = { ...o, x: clamp(o.x + dx, 0, 1 - o.w), y: clamp(o.y + dy, 0, 1 - o.h) };
    else {
      n = { ...o, w: clamp(o.w + dx, 0.03, 1 - o.x), h: clamp(o.h + dy, 0.03, 1 - o.y) };
      if (d.key === "photo" && (o.shape || "circle") === "circle") { const ratio = size.w / size.h; n.h = clamp(n.w * ratio, 0.03, 1 - o.y); }
    }
    setZones((z) => ({ ...z, [d.key]: n }));
  };
  const up = () => { drag.current = null; };
  const scale = size.w / (size.nat || 1);

  return (
    <div ref={box} onPointerMove={move} onPointerUp={up} onPointerCancel={up} className="relative touch-none select-none overflow-hidden rounded-xl shadow-2xl ring-1 ring-[hsl(var(--gold))]/30" data-testid="studio-canvas">
      {templateUrl ? <img ref={imgRef} src={templateUrl} alt="" className="block w-full" draggable={false} onLoad={(e) => setSize((s) => ({ ...s, nat: e.target.naturalWidth }))} /> : <div className="aspect-[3/4] animate-pulse bg-secondary" />}
      {["photo", "name", "date"].map((key) => {
        const z = zones[key]; if (!z) return null;
        const isPhoto = key === "photo", circle = isPhoto && (z.shape || "circle") === "circle";
        const text = key === "name" ? name : date;
        const bw = z.w * size.w, bh = z.h * size.h, on = active === key;
        return (
          <div key={key} onPointerDown={(e) => down(e, "move", key)} style={{ left: `${z.x * 100}%`, top: `${z.y * 100}%`, width: `${z.w * 100}%`, height: `${z.h * 100}%` }}
            className={`absolute cursor-move ${on ? "z-20" : "z-10"}`} data-testid={`studio-layer-${key}`}>
            <div className={`h-full w-full overflow-hidden ${circle ? "rounded-full" : ""}`} style={isPhoto ? (z.ring ? { boxShadow: `inset 0 0 0 ${Math.max(2, bw / 20)}px ${z.ring_color || "#F5C542"}` } : undefined) : (z.clear && fills[key] ? { background: fills[key] } : undefined)}>
              {isPhoto ? (photoUrl ? <img src={photoUrl} alt="" draggable={false} className="h-full w-full object-cover" style={{ objectPosition: `${(z.fx ?? 0.5) * 100}% ${(z.fy ?? 0.3) * 100}%`, transform: `scale(${z.zoom || 1})`, transformOrigin: `${(z.fx ?? 0.5) * 100}% ${(z.fy ?? 0.3) * 100}%` }} /> : <Monogram name={name || "?"} className="h-full w-full" />)
                : (text ? <TextLayer z={z} text={text} scale={scale} boxW={bw} boxH={bh} /> : <div className="flex h-full w-full items-center justify-center text-[0.65rem] uppercase tracking-wider text-white/70 [text-shadow:0_1px_2px_#000]">{LABEL[key]} here</div>)}
            </div>
            <div className={`pointer-events-none absolute inset-0 border-2 ${circle ? "rounded-full" : "rounded-sm"} ${on ? "border-[hsl(var(--gold))]" : "border-dashed border-white/50 opacity-0 hover:opacity-100"}`} />
            {on && <span className="absolute -top-5 left-0 rounded bg-[hsl(var(--gold))] px-1.5 text-[0.6rem] font-700 uppercase tracking-wider text-[#0A192F]">{LABEL[key]} · drag to move</span>}
            {on && <span onPointerDown={(e) => down(e, "resize", key)} className="absolute -bottom-2 -right-2 h-5 w-5 cursor-nwse-resize rounded-full border-2 border-white bg-[hsl(var(--gold))] shadow" data-testid={`studio-resize-${key}`} />}
          </div>
        );
      })}
    </div>
  );
}
